import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PLANS } from '@/content';
import ZoomImage from '@/components/ZoomImage';
import { track } from '@/lib/track';
import RedO from '@/components/RedO';

export default function Plans({ onEnquire }) {
  const [config, setConfig] = useState(PLANS.configurations[0]);
  const [plan, setPlan] = useState(PLANS.floorPlans[1]);

  // preload the neighbouring floor plans so switching feels instant
  useEffect(() => {
    PLANS.floorPlans.forEach((p) => {
      if (p.id !== plan.id) { const im = new Image(); im.src = p.image; }
    });
  }, [plan]);

  const pickConfig = (c) => {
    setConfig(c);
    track('config_select', { configuration: c.label });
  };
  const pickPlan = (p) => {
    setPlan(p);
    track('floorplan_view', { plan: p.id, configuration: config.label });
  };

  return (
    <section id="plans" data-testid="plans-section" className="scroll-mt-20 bg-white py-20 sm:py-28" aria-labelledby="plans-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="eyebrow">{PLANS.eyebrow}</p>
        <h2 id="plans-heading" className="headline mt-3 text-3xl sm:text-5xl">
          <RedO text={PLANS.headline[0]} />
          <span className="mt-1 block text-[#646A72]"><RedO text={PLANS.headline[1]} /></span>
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_1.15fr]">
          {/* configuration column */}
          <div className="min-w-0">
            <div className="flex rounded-full border border-[#E4E6EB] bg-[#FAF9F6] p-1.5" role="tablist" aria-label="Configuration">
              {PLANS.configurations.map((c) => (
                <button
                  key={c.id}
                  data-testid={`floorplan-tab-${c.id}`}
                  role="tab"
                  aria-selected={config.id === c.id}
                  onClick={() => pickConfig(c)}
                  className={`min-h-[48px] flex-1 rounded-full font-display text-sm font-extrabold uppercase tracking-[0.14em] transition-[background-color,color] duration-300 ${
                    config.id === c.id ? 'bg-[#C92F4B] text-white' : 'text-[#404145]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={config.id}
                data-testid="config-panel"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="mt-7"
              >
                <p className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-[#A41225]">{config.tag}</p>
                <p className="mt-3 text-base leading-relaxed text-[#404145]">{config.text}</p>

                <dl className="mt-6 space-y-3 border-t border-[#E4E6EB] pt-6">
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-sm font-semibold text-[#646A72]">RERA Carpet Area</dt>
                    <dd className="text-right text-sm font-bold text-[#1C1D21]" data-testid="config-carpet-area">{config.carpetArea || 'On request'}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-sm font-semibold text-[#646A72]">Starting Price</dt>
                    <dd className="text-right text-sm font-bold text-[#A41225]" data-testid="config-price">{config.price || 'On request'}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-sm font-semibold text-[#646A72]">Availability</dt>
                    <dd className="text-right text-sm font-bold text-[#15803D]" data-testid="config-availability">{config.availability}</dd>
                  </div>
                </dl>
                <p className="mt-4 text-xs leading-relaxed text-[#646A72]">{PLANS.note}</p>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <button
                    data-testid="plans-request-price-btn"
                    onClick={() => { track('book_visit', { placement: 'plans_price', configuration: config.label }); onEnquire({ interest: config.label, topic: 'price' }); }}
                    className="btn-primary flex-1 !text-xs"
                  >
                    Request Current Price
                  </button>
                  <button
                    data-testid="plans-book-visit-btn"
                    onClick={() => { track('book_visit', { placement: 'plans_visit', configuration: config.label }); onEnquire({ interest: config.label }); }}
                    className="btn-ghost flex-1 !text-xs"
                  >
                    Book a Visit
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* plan viewer column */}
          <div className="min-w-0">
            <div className="scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Floor plans">
              {PLANS.floorPlans.map((p) => (
                <button
                  key={p.id}
                  data-testid={`plan-select-${p.id}`}
                  role="tab"
                  aria-selected={plan.id === p.id}
                  onClick={() => pickPlan(p)}
                  className={`min-h-[44px] shrink-0 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-[background-color,border-color,color] duration-300 ${
                    plan.id === p.id ? 'border-[#C92F4B] bg-[#C92F4B]/5 text-[#A41225]' : 'border-[#E4E6EB] bg-white text-[#404145]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, scale: 0.985 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ZoomImage
                    src={plan.image}
                    alt={`Official ${plan.label} of Aarohi Complex, Codename Metro One Navade`}
                    testid="plan-viewer"
                    aspect="1686 / 2274"
                  />
                </motion.div>
              </AnimatePresence>
              <p className="mt-3 text-center text-xs text-[#646A72]">
                Official drawing from the approved brochure. Pinch, scroll or drag to inspect — dimensions unchanged.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
